/**
 * Marker stamped onto the plain object returned by Book's axios `processError`
 * path (`apps/book/src/plugins/axios.js`). Nested axios-retry interceptor
 * re-entries key on this marker (MOVEPRO-BOOK-AB); Sentry `beforeSend` also
 * drops unhandled rejections of these objects (MOVEPRO-BOOK-AF) because the
 * interceptor already decided whether to `captureException`.
 */
export const BOOK_PROCESSED_ERROR_MARKER = '__bookProcessedError' as const;

export function isBookProcessedError(value: unknown): boolean {
    return Boolean(
        value && typeof value === 'object' && (value as Record<string, unknown>)[BOOK_PROCESSED_ERROR_MARKER] === true,
    );
}

/**
 * Empty plain-object promise rejections (`Promise.reject({})` / `throw {}`)
 * produce Sentry titles like "Object captured as promise rejection with keys:
 * [object has no keys]" with no stack. Book's axios path always stamps
 * `__bookProcessedError` + message/code keys, so `{}` is never ours —
 * typically browser-extension noise (MOVEPRO-BOOK-AG; same Review session as
 * sibling BOOK-AH / PayPal Honey — extension URL filtering lives in #1794).
 */
export function isEmptyPlainObjectRejection(value: unknown): boolean {
    if (value === null || typeof value !== 'object') {
        return false;
    }
    if (value instanceof Error) {
        return false;
    }
    // Arrays / Dates / etc. have no useful enumerable keys either in some
    // cases — only drop true plain objects created as `{}`.
    const proto = Object.getPrototypeOf(value);
    if (proto !== Object.prototype && proto !== null) {
        return false;
    }
    return Object.keys(value as object).length === 0;
}

/**
 * Whether a Sentry event should be dropped because it is a re-report of an
 * axios-processed plain object, or a useless empty-object unhandled rejection
 * (unhandled rejection or accidental `captureException` of the processed shape).
 */
export function shouldDropBookProcessedSentryEvent(hint: { originalException?: unknown } | undefined): boolean {
    const original = hint?.originalException;
    return isBookProcessedError(original) || isEmptyPlainObjectRejection(original);
}
